import React from "react";
import { type StyleProp, StyleSheet, View, type ViewStyle } from "react-native";
import Svg, { Defs, LinearGradient, Rect, Stop } from "react-native-svg";

import { palette } from "../styles";

type GradientProps = {
  direction:
    | "top"
    | "bottom"
    | "left"
    | "right"
    | "topLeft"
    | "topRight"
    | "bottomLeft"
    | "bottomRight";
  height?: number;
  width?: string | number;
  zIndex?: number;
  color?: string;
  points?: { offset: string; stopOpacity: number; stopColor?: string }[];
  style?: StyleProp<ViewStyle>;
};

const bottomStyle = { bottom: 0, left: 0, right: 0 };
const topStyle = { left: 0, right: 0, top: 0 };

const styles = StyleSheet.create({
  container: { overflow: "hidden", position: "absolute" },
  bottom: bottomStyle,
  left: { bottom: 0, left: 0, right: 0, top: 0 },
  right: { bottom: 0, left: 0, right: 0, top: 0 },
  top: topStyle,
  topLeft: topStyle,
  topRight: topStyle,
  bottomLeft: bottomStyle,
  bottomRight: bottomStyle,
});

const defaultPoints = [
  { offset: "0%", stopOpacity: 0.5 },
  { offset: "100%", stopOpacity: 0 },
];

const getLinearGradientProps = ({
  direction,
  height,
}: Pick<GradientProps, "height" | "direction">) => {
  switch (direction) {
    case "top":
      return { x1: "0%", x2: "0%", y1: "0%", y2: height };
    case "bottom":
      return { x1: "0%", x2: "0%", y1: height, y2: "0%" };
    case "left":
      return { x1: "0%", x2: "100%", y1: "0%", y2: height };
    case "right":
      return { x1: "100%", x2: "0%", y1: "0%", y2: height };
    case "topLeft":
      return { x1: "0%", x2: "100%", y1: height, y2: "0%" };
    case "topRight":
      return { x1: "100%", x2: "0%", y1: height, y2: "0%" };
    case "bottomLeft":
      return { x1: "0%", x2: "100%", y1: "0%", y2: height };
    case "bottomRight":
      return { x1: "100%", x2: "0%", y1: "0%", y2: height };
  }
};

const Gradient = ({
  direction,
  zIndex = 0,
  height = 100,
  width = "100%",
  color = palette.common.black,
  points = defaultPoints,
  style,
}: GradientProps) => {
  const linearGradientProps = getLinearGradientProps({ direction, height });

  const gradientId = `gradient-id-${Math.random() * 1000}`;

  return (
    <View pointerEvents="none" style={[styles[direction], style, styles.container, { zIndex }]}>
      <Svg height={height} width={width}>
        <Defs>
          <LinearGradient gradientUnits="userSpaceOnUse" id={gradientId} {...linearGradientProps}>
            {points.map(({ offset, stopOpacity, stopColor }) => (
              <Stop
                key={`${offset}-${stopColor}`}
                offset={offset}
                stopColor={stopColor ? stopColor : color}
                stopOpacity={stopOpacity}
              />
            ))}
          </LinearGradient>
        </Defs>

        <Rect fill={`url(#${gradientId})`} height={height} width={width} x="0" y="0" />
      </Svg>
    </View>
  );
};

export default React.memo(Gradient);
